import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/joy";
import DeleteIcon from "@mui/icons-material/Delete";
import Page from "../components/Pages/Page";
import PageHeader from "../components/PageHeader";
import { useOrgSettingsContext } from "../providers/OrgSettingsProvider";
import { useDeleteOrganization } from "../services/api";

const OrgSettingsAdvanced: React.FC = () => {
  const { user, organizationDetails } = useOrgSettingsContext();
  const deleteOrganizationAPI = useDeleteOrganization();

  const [deleteOrgModalOpen, setDeleteOrgModalOpen] = useState(false);
  const [deletingOrganization, setDeletingOrganization] = useState(false);
  const [deleteOrgConfirmName, setDeleteOrgConfirmName] = useState("");
  const [deleteOrgError, setDeleteOrgError] = useState<string | null>(null);

  const closeDeleteModal = () => {
    if (deletingOrganization) return;
    setDeleteOrgModalOpen(false);
    setDeleteOrgConfirmName("");
    setDeleteOrgError(null);
  };

  return (
    <Page>
      <PageHeader>
        <Typography level="h1">Advanced</Typography>
        <Typography level="body-sm" color="neutral">
          Irreversible and destructive actions
        </Typography>
      </PageHeader>

      <Stack spacing={3}>
        <Card variant="outlined" sx={{ borderColor: "danger.300" }}>
          <CardContent>
            <Stack spacing={2}>
              <Typography level="title-lg" sx={{ color: "danger.600" }}>
                Danger Zone
              </Typography>
              <Divider />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{
                  alignItems: { sm: "center" },
                  justifyContent: "space-between",
                }}
              >
                <Box>
                  <Typography level="title-sm">
                    Delete this organization
                  </Typography>
                  <Typography level="body-sm">
                    Permanently deletes the organization, all workspaces,
                    domains, and subscriptions. All members will be unlinked.
                    This cannot be undone.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="danger"
                  onClick={() => setDeleteOrgModalOpen(true)}
                  startDecorator={<DeleteIcon />}
                  sx={{ flexShrink: 0 }}
                >
                  Delete Organization
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Modal open={deleteOrgModalOpen} onClose={closeDeleteModal}>
        <ModalDialog
          size="md"
          variant="outlined"
          sx={{ maxWidth: 480, width: "100%" }}
        >
          <DialogTitle>Delete Organization</DialogTitle>
          <Stack spacing={3}>
            {deleteOrgError && (
              <Alert color="danger" variant="soft">
                {deleteOrgError}
              </Alert>
            )}
            <Alert color="warning" variant="soft">
              This will permanently delete all workspaces, domains,
              subscriptions, and unlink all members. This action cannot be
              undone.
            </Alert>
            <FormControl>
              <FormLabel>
                Type <strong>{organizationDetails?.name}</strong> to confirm
              </FormLabel>
              <Input
                placeholder="Organization name"
                value={deleteOrgConfirmName}
                onChange={(e) => setDeleteOrgConfirmName(e.target.value)}
                disabled={deletingOrganization}
              />
            </FormControl>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="neutral"
                onClick={closeDeleteModal}
                disabled={deletingOrganization}
              >
                Cancel
              </Button>
              <Button
                variant="solid"
                color="danger"
                loading={deletingOrganization}
                disabled={
                  deletingOrganization ||
                  deleteOrgConfirmName !== organizationDetails?.name
                }
                onClick={async () => {
                  if (!user.organization_id) return;
                  setDeletingOrganization(true);
                  setDeleteOrgError(null);
                  try {
                    await deleteOrganizationAPI(user.organization_id);
                    window.location.href = "/";
                  } catch (err: any) {
                    setDeleteOrgError(
                      err.message || "Failed to delete organization",
                    );
                    setDeletingOrganization(false);
                  }
                }}
              >
                Delete Organization
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Page>
  );
};

export default OrgSettingsAdvanced;
